import {
    Navigate,
    Routes,
    Route,
    useNavigate,
    Outlet,
    useMatch
} from "react-router-dom";

import { useState, useEffect, useMemo, useContext, useCallback, useRef, lazy, Suspense } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { AppNavBar } from "./components/AppNavBar";
import StatusMessages, { StatusMessage, StatusMessageType } from "../../components/shared/StatusMessages";
import { SleepLogHttpServiceProvider } from "../../components/shared/sleep-log-http-service.provider";
import { SleepLogSettingsServiceContext, SleepLogSettingsServiceProvider } from "../../lib/services/sleep-log-settings-service.provider";
import { LoginState } from "../../types/CommonInterfaces";
import { AccountHttpServiceContext, AccountHttpServiceProvider } from "../../components/shared/account-http-service.provider";
import { AccountServiceContext, AccountServiceProvider } from "../../components/shared/account-service.provider";
import { LoginStateContext } from "./login-state.context";
import { HttpException } from "../../types/Exceptions";

import '../../styles/app-controls.css';
import '../../styles/app-page.css';
import styles from './styles/app.module.css';
import { AppState } from "../../types/AppState";
import { AppStateContext } from "./AppStateContext";
import { logger } from "../../lib/logger/EventLogger";
import { TagSuggestionServiceProvider } from "../../lib/services/TagSuggestionsService.provider";
import { SleepLogSummariesServiceProvider } from "../../lib/services/sleep-log-summaries-service.provider";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { SleepchartNavBar } from "../../components/shared/BottomNavBar";
import classNames from "classnames";
import { SleepLogSettingsModal } from "../../components/shared/SleepLogSettingsModal";
import { BuildVersion } from "../../constants/build-version";
import { useMainScrollRestoration } from "./useMainScrollRestoration";

// Lazy load components
const SignupView = lazy(() => import("../signup/SignupView"));
const ImportView = lazy(() => import("../import/ImportView"));
const SleepLogsView = lazy(() => import("../sleep-logs/SleepLogsView"));
const AccountSettings = lazy(() => import("../settings/AccountSettings"));
const AuthorizeDevice = lazy(() => import("../authorize/AuthorizeDevice"));
const LoginView = lazy(() => import("../login/LoginView"));
const AccountDevices = lazy(() => import("../devices/AccountDevices"));
const ResetPassword = lazy(() => import("../password-reset/reset-password.component"));
const ForgotPassword = lazy(() => import("../forgot-password/forgot-password.component"));

function AppStateProvider({ children } : { children: React.ReactNode }) {

    const [loginState, setLoginState] = useState<LoginState>(() => {
        let username = window.localStorage.getItem("username");
        console.log(`Read user: ${username} from local storage.`);

        if (username) {
            logger.setUser(username);
            logger.log("Session Started");

            return { loggedIn: true, username: username };
        }
        else {
            return { loggedIn: false };
        }
    });

    const loginStateContext: [LoginState, (value: LoginState) => void] = useMemo(() => {
        return [loginState, setLoginState];
    }, [loginState, setLoginState]);

    const [appState, setAppState] = useState<AppState>(() => ({ sessionId: uuidv4() }));

    const appStateContext: [AppState, (value: AppState) => void] = useMemo(() => [appState, setAppState]
        , [appState, setAppState]);

    return (
        <AppStateContext.Provider value={appStateContext}>
            <LoginStateContext.Provider value={loginStateContext}>
                <AccountHttpServiceProvider>
                    <AccountServiceProvider>
                        <SleepLogHttpServiceProvider>
                            <SleepLogSettingsServiceProvider>
                                <TagSuggestionServiceProvider>
                                    <SleepLogSummariesServiceProvider>
                                        {children}
                                    </SleepLogSummariesServiceProvider>
                                </TagSuggestionServiceProvider>
                            </SleepLogSettingsServiceProvider>
                        </SleepLogHttpServiceProvider>
                    </AccountServiceProvider>
                </AccountHttpServiceProvider>
            </LoginStateContext.Provider>
        </AppStateContext.Provider>
    );
}

export function App() {

    return (
        <AppStateProvider>
            <AppRoot />
        </AppStateProvider>
    );
}

type Selection = "Diary" | "Dashboard" | "Explore"

export function AppRoot() {
    let navigate = useNavigate();
    const [loginState, setLoginState] = useContext(LoginStateContext);
    const sleepLogSettingsService = useContext(SleepLogSettingsServiceContext)!;
    const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([]);
    const accountHttpService = useContext(AccountHttpServiceContext)!;
    const accountService = useContext(AccountServiceContext)!;
    const [showSleepLogSettings, setShowSleepLogSettings] = useState(false);   
    const mainRef = useRef<HTMLElement | null>(null);
    useMainScrollRestoration(mainRef);

    useEffect(() => {
        // TODO: This should be moved down to only the routes that need settings
        if (!loginState.loggedIn) {
            return;
        }

        (async () => {
            try {
                const sleepLogSettingsPromise = sleepLogSettingsService.fetch();
                const accountSettingsPromise = accountService.fetchProperties();

                await Promise.all([sleepLogSettingsPromise, accountSettingsPromise]);
            }
            catch (e) {
                if (e instanceof HttpException && e.statusCode === 401) {
                    addStatus({
                        type: StatusMessageType.Info,
                        msg: `Your session has expired. You'll need to sign in again.`
                    });

                    await accountHttpService.clearSessionCookie();
                    logoutUser(false /* showStatus */);
                }
                else {
                    console.log("Fetching settings failed.");
                }
            }
        })();

    }, [loginState]);

    const addStatus = useCallback((msg: StatusMessage) => {
        // slice at 10 to limit the number of errors.

        if (msg.type === StatusMessageType.Fail || msg.type === StatusMessageType.UnexpectedFail) {
            logger.log("User Hit Error", { msg: msg.msg, type: msg.type === StatusMessageType.UnexpectedFail ? "Unexpected" : "Expected" });
        }

        setStatusMessages(prev => [msg, ...prev.slice(0, 9)]);
    }, []);

    const loginUser = useCallback((username: string) => {
        logger.log("User Logged In");

        window.localStorage.setItem("username", username);
        setLoginState({ loggedIn: true, username: username });
        logger.setUser(username);

        navigate("/sleep-logs");
    }, [navigate, setLoginState]);

    const logoutUser = useCallback(async (showStatus = true) => {
        if (!loginState.loggedIn) {
            return;
        }

        // TODO: Does cookie need to be deleted as well?
        window.localStorage.removeItem("username");

        try {
            await accountHttpService.deleteSession();
        } catch (e: any) {
            console.log("Removing session failed. It will be cleaned up later.");
        }

        if (showStatus) {
            addStatus({
                msg: "Logged out successfully.",
                type: StatusMessageType.Success
            });
        }

        setLoginState({ loggedIn: false });

        logger.log("User Logged Out");

        navigate("/login");
    }, [loginState.loggedIn, accountHttpService, navigate, setLoginState, addStatus]);

    useEffect(() => {
        window.onerror = (event: string | Event) => {
            console.log("Uncaught error");

            addStatus({
                type: StatusMessageType.UnexpectedFail,
                msg: `Unexpected error occurred. Reason: ${event}`
            })
        }
    }, []);

    const closeStatus = useCallback(() => {
        setStatusMessages(prev => [...prev.slice(1)]);
    }, []);

    const handleTabSelection = useCallback((view: Selection) => {
        switch (view) {
            case 'Diary':
                navigate('/sleep-logs');
                break;
            case 'Dashboard':
                navigate('/dashboard');
                break;
            case 'Explore':
                navigate('/explore');
                break;
            default:
                break;
        }
    }, []);

    const diaryMatch = useMatch({ path: '/sleep-logs/*', end: false });
    const dashboardMatch = useMatch({ path: '/dashboard/*', end: false });
    const exploreMatch = useMatch({ path: '/explore/*', end: false });
    const selectedTab: Selection | undefined = diaryMatch
        ? 'Diary'
        : dashboardMatch
        ? 'Dashboard'
        : exploreMatch
        ? 'Explore'
        : undefined;
    
    const closeSettings = useCallback(() => {
        setShowSleepLogSettings(false);
    }, []);

    const scrollMainToTop = useCallback(() => {
        const el = mainRef.current;
        if (el) {
            el.scrollTo({ top: 0 });
        }
    }, []);

    return (
        <>
            <StatusMessages
                messages={statusMessages}
                closeStatus={closeStatus}
            />

            <div className={styles["app-container-background"]}>
                <div className={styles["app-container"]}>
                    <header className={styles["app-nav-container"]}>
                        <AppNavBar
                            loginState={loginState}
                            onLogout={logoutUser}
                            addStatus={addStatus}
                        />
                    </header>

                    <main className={styles["app-main-container"]} ref={mainRef}>
                        <Routes>

                            <Route
                                path="/"
                                element={
                                    <Outlet />
                                }
                            >
                                <Route index
                                    element={loginState.loggedIn ?
                                        <Navigate to="/sleep-logs" />
                                        :
                                        <Navigate to="/home"
                                        />}
                                />

                                <Route path="home"
                                    element={loginState.loggedIn ?
                                        <Navigate to="/sleep-logs" />
                                        :
                                        <Navigate to="/login" />
                                    }
                                />

                                <Route path="authorize" element={
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <AuthorizeDevice addStatus={addStatus} />
                                    </Suspense>
                                } />

                                <Route path="login"
                                    element={loginState.loggedIn ?
                                        <Navigate to="/sleep-logs" />
                                        :
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <LoginView setLoggedIn={loginUser} addStatus={addStatus} />
                                        </Suspense>
                                    }
                                />

                                <Route path="signup"
                                    element={loginState.loggedIn ?
                                        <Navigate to="/sleep-logs" />
                                        :
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <SignupView addStatus={addStatus} />
                                        </Suspense>
                                    }
                                />

                                <Route path="password-reset" element={
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <ResetPassword addStatus={addStatus} />
                                    </Suspense>
                                } />

                                <Route path="forgot-password" element={
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <ForgotPassword addStatus={addStatus} />
                                    </Suspense>
                                } />

                                <Route path="import"
                                    element={loginState.loggedIn ?
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <ImportView addStatus={addStatus} />
                                        </Suspense>
                                        :
                                        <Navigate to='/login' />
                                    }
                                />

                                <Route path="sleep-logs/*"
                                    element={loginState.loggedIn ?
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <SleepLogsView loginState={loginState} addStatus={addStatus} onScrollMainToTop={scrollMainToTop} />
                                        </Suspense>
                                        :
                                        <Navigate to='/login' />
                                    }
                                />

                                <Route path="dashboard/*"
                                    element={loginState.loggedIn ?
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <SleepLogsView loginState={loginState} addStatus={addStatus} onScrollMainToTop={scrollMainToTop} />
                                        </Suspense>
                                        :
                                        <Navigate to='/login' />
                                    }
                                />

                                <Route path="explore/*"
                                    element={loginState.loggedIn ?
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <SleepLogsView loginState={loginState} addStatus={addStatus} onScrollMainToTop={scrollMainToTop} />
                                        </Suspense>
                                        :
                                        <Navigate to='/login' />
                                    }
                                />

                                <Route path="settings"
                                    element={loginState.loggedIn ?
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <AccountSettings addStatus={addStatus} />
                                        </Suspense>
                                        :
                                        <Navigate to='/login' />
                                    }
                                />

                                <Route path="devices"
                                    element={loginState.loggedIn ?
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <AccountDevices addStatus={addStatus} loginState={loginState} />
                                        </Suspense>
                                        :
                                        <Navigate to='/login' />
                                    }
                                />

                                <Route
                                    path="*"
                                    element={
                                        <main>
                                            <div className="container mt-5 text-center">
                                                <h1 className="fs-1 mb-4">Page Not Found</h1>
                                                <p className="fs-4">The requested path <code>{window.location.pathname}</code> could not be found.</p>
                                                <p className="mt-3"></p></div>
                                        </main>
                                    }
                                >
                                </Route>
                            </Route>
                        </Routes>

                        <div className={
                            classNames(styles["footer"], "bg-dark p-3 text-light text-center")}
                        >
                            <p>Build version {BuildVersion.VERSION}</p>
                        </div>
                    </main>

                    <footer className={styles["app-bottom-container"]}>
                        { loginState.loggedIn &&
                            <SleepchartNavBar
                                selected={selectedTab}
                                onSelect={(view) => handleTabSelection(view)}
                            />
                        }
                    </footer>
                </div>
            </div>

            <SleepLogSettingsModal
                show={showSleepLogSettings}
                onClose={closeSettings}
                addStatus={addStatus}
                loginState={loginState}
            />
        </>
    );
}
